/**
 * Default JSON model for the dockable editor layout.
 *
 * flexlayout-react model structure:
 *  - Top-level "row" renders **horizontally** (left ↔ right columns).
 *  - Nested "row" children render in the **opposite** orientation (vertically).
 *
 * Default arrangement (VS Code–style editor column):
 *   ┌──────────────────┬──────────────────┐
 *   │  Code Editor tabs │                  │
 *   │  (tabset, 2/3)    │  PDF Preview     │
 *   ├──────────────────┤  (tabset)        │
 *   │  Console          │                  │
 *   │  (tabset, 1/3)    │                  │
 *   └──────────────────┴──────────────────┘
 */

import type { IJsonModel } from "flexlayout-react"

/**
 * Component identifiers used by the factory function.
 * When adding new panel types, register them here.
 */
export const PANEL_COMPONENTS = {
  CODE_EDITOR: "code-editor",
  PDF_PREVIEW: "pdf-preview",
  EPUB_PREVIEW: "epub-preview",
  CONSOLE: "console",
  IMAGE: "image",
} as const

export type PanelComponent = (typeof PANEL_COMPONENTS)[keyof typeof PANEL_COMPONENTS]

/** Panels that can be reopened from the View menu */
export const RESTORABLE_PANELS: PanelComponent[] = [
  PANEL_COMPONENTS.CODE_EDITOR,
  PANEL_COMPONENTS.CONSOLE,
  PANEL_COMPONENTS.PDF_PREVIEW,
  // PANEL_COMPONENTS.EPUB_PREVIEW,
]

export const PANEL_LABELS: Record<PanelComponent, string> = {
  [PANEL_COMPONENTS.CODE_EDITOR]: "Code Editor",
  [PANEL_COMPONENTS.CONSOLE]: "Console",
  [PANEL_COMPONENTS.PDF_PREVIEW]: "PDF Viewer",
  [PANEL_COMPONENTS.EPUB_PREVIEW]: "eBook Viewer",
  [PANEL_COMPONENTS.IMAGE]: "Image",
}

/** sessionStorage key for persisting user layout within a browser session */
export const LAYOUT_STORAGE_KEY = "rudrise-editor-layout"

/** sessionStorage key for last layout activity (used for inactivity reset) */
export const LAYOUT_LAST_ACTIVE_KEY = "rudrise-editor-layout-last-active"

/** Bumped when the default dock structure changes (invalidates old persisted layouts). */
export const LAYOUT_SCHEMA_VERSION = 3
export const LAYOUT_SCHEMA_KEY = "rudrise-editor-layout-schema"

/** Reset layout to default after this many ms of inactivity */
export const LAYOUT_INACTIVITY_MS = 30 * 60 * 1000

export const EDITOR_COLUMN_ID = "editor-column"
export const EDITOR_TABSET_ID = "editor-tabset"
export const CONSOLE_TABSET_ID = "console-tabset"
export const PDF_TABSET_ID = "pdf-tabset"

/** Keeps the editor tabset alive — flexlayout drops empty tabsets on load. */
export const LAYOUT_EDITOR_PLACEHOLDER_TAB_ID = "tab-editor-placeholder"
export const LAYOUT_EDITOR_PLACEHOLDER_FILE_ID = "__layout_editor_placeholder__"

/** Vertical split inside the editor column (editor above console). */
export const EDITOR_CONSOLE_SPLIT = {
  editor: 67,
  console: 33,
} as const

/**
 * Returns the default layout JSON.
 * Called once on first load or when resetting the layout.
 */
export function getDefaultLayoutJson(): IJsonModel {
  return {
    global: {
      tabEnableClose: true,
      tabEnableRename: false,
      tabSetEnableMaximize: true,
      tabSetEnableDrop: true,
      tabSetEnableDrag: true,
      tabSetEnableDivide: true,
      tabSetMinWidth: 100,
      tabSetMinHeight: 80,
      splitterSize: 4,
      splitterExtra: 4,
      enableEdgeDock: true,
    },
    borders: [],
    layout: {
      type: "row",
      weight: 100,
      children: [
        /* ── LEFT COLUMN: editor (2/3) stacked above console (1/3) ── */
        {
          type: "row",
          id: EDITOR_COLUMN_ID,
          weight: 60,
          children: [
            {
              type: "tabset",
              weight: EDITOR_CONSOLE_SPLIT.editor,
              id: EDITOR_TABSET_ID,
              children: [
                {
                  type: "tab",
                  id: LAYOUT_EDITOR_PLACEHOLDER_TAB_ID,
                  name: "\u00a0",
                  component: PANEL_COMPONENTS.CODE_EDITOR,
                  config: { fileId: LAYOUT_EDITOR_PLACEHOLDER_FILE_ID },
                },
              ],
            },
            {
              type: "tabset",
              weight: EDITOR_CONSOLE_SPLIT.console,
              id: CONSOLE_TABSET_ID,
              children: [
                {
                  type: "tab",
                  id: "tab-console",
                  name: "Console",
                  component: PANEL_COMPONENTS.CONSOLE,
                },
              ],
            },
          ],
        },

        /* ── RIGHT COLUMN: PDF preview (full height) ── */
        {
          type: "tabset",
          weight: 40,
          id: PDF_TABSET_ID,
          children: [
            {
              type: "tab",
              id: "tab-pdf",
              name: "PDF",
              component: PANEL_COMPONENTS.PDF_PREVIEW,
            },
          ],
        },
      ],
    },
  }
}
